import React, { useState } from "react";
import { Button, Card, Dropdown, Form, Modal, Spinner } from "react-bootstrap";
import { connect } from "react-redux";
import firebase from "firebase";
import { toast } from "react-toastify";
import { useCollection } from "react-firebase-hooks/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faEllipsisH,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import ReactExport from "react-export-excel";

function AsIsConclusions(props) {
  const { darkTheme, event_id, project_id } = props;
  const user = firebase.auth().currentUser;

  const [showConclusionForm, setShowConclusionForm] = useState(false);
  const [conclusion, setConclusion] = useState({ title: "", description: "" });
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState(false);

  const ExcelFile = ReactExport.ExcelFile;
  const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
  const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

  const resetConclusion = () => {
    setConclusion({ id: "", title: "", description: "" });
  };

  const [conclusionsSnapshot, conclusionsLoading, conslusionsError] =
    useCollection(
      firebase
        .firestore()
        .collection(`events/${event_id}/bboa/${project_id}/as_is_conclusions`)
    );

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setSending(true);

    if (editing) {
      if (conclusion?.id === "") {
        toast.warning("Error editing conclusion, please refresh.", {
          position: toast.POSITION.TOP_CENTER,
          hideProgressBar: true,
          autoClose: 3000,
        });
        return;
      }
    }

    if (conclusion?.title === "" || conclusion?.description === "") {
      setError(true);
      setSending(false);
      return;
    }

    const asIsConclusionRef = firebase
      .firestore()
      .collection(`events/${event_id}/bboa/${project_id}/as_is_conclusions`);

    if (editing) {
      asIsConclusionRef
        .doc(`${conclusion?.id}`)
        .update(
          {
            title: conclusion?.title ?? "",
            description: conclusion?.description ?? "",
            updated_at: firebase.firestore.FieldValue.serverTimestamp(),
          },
          {
            merge: true,
          }
        )
        .then(() => {
          toast.success("Conclusion updated successfully", {
            position: toast.POSITION.TOP_CENTER,
            hideProgressBar: true,
            autoClose: 3000,
          });
        })
        .catch((e) => {
          console.error("Conclusion error: ", e);
          setSending(false);

          toast.warning("Error adding conclusion: " + e, {
            position: toast.POSITION.TOP_CENTER,
            hideProgressBar: true,
            autoClose: 3000,
          });
        })
        .finally(() => {
          setSending(false);
          resetConclusion();
          setShowConclusionForm(false);
          setEditing(false);
        });
    } else {
      asIsConclusionRef
        .add({
          title: conclusion?.title ?? "",
          description: conclusion?.description ?? "",
          created_at: firebase.firestore.FieldValue.serverTimestamp(),
          created_by: {
            name: user.displayName,
            email: user.email,
            id: user.uid,
          },
        })
        .then(() => {
          toast.success("Conclusion added successfully", {
            position: toast.POSITION.TOP_CENTER,
            hideProgressBar: true,
            autoClose: 3000,
          });
        })
        .catch((e) => {
          console.error("Conclusion error: ", e);
          setSending(false);

          toast.warning("Error adding conclusion: " + e, {
            position: toast.POSITION.TOP_CENTER,
            hideProgressBar: true,
            autoClose: 3000,
          });
        })
        .finally(() => {
          setSending(false);
          resetConclusion();
          setShowConclusionForm(false);
        });
    }
  };

  const handleDeleteConclusion = () => {
    if (!!conclusion && conclusion?.id != "") {
      // do nothing
    } else {
      toast.warning("Error deleting conclusion, please refresh.", {
        position: toast.POSITION.TOP_CENTER,
        hideProgressBar: true,
        autoClose: 3000,
      });
      return;
    }
    setDeleting(true);

    const asIsConclusionRef = firebase
      .firestore()
      .collection(`events/${event_id}/bboa/${project_id}/as_is_conclusions`);

    asIsConclusionRef
      .doc(`${conclusion?.id}`)
      .delete()
      .then(() => {
        toast.dark("Conclusion deleted successfully", {
          position: toast.POSITION.TOP_CENTER,
          hideProgressBar: true,
          autoClose: 3000,
        });
      })
      .catch((e) => {
        console.error("Conclusion error: ", e);
        setDeleting(false);

        toast.warning("Error deleting conclusion: " + e, {
          position: toast.POSITION.TOP_CENTER,
          hideProgressBar: true,
          autoClose: 3000,
        });
      })
      .finally(() => {
        setDeleting(false);
        resetConclusion();
        setShowDeleteModal(false);
      });
  };

  const convertSecondstoMilliSeconds = (seconds, nanoseconds = 0) => {
    return Math.floor(seconds * 1000 + nanoseconds / 1000000);
  };

  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <a
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="text-muted"
    >
      <FontAwesomeIcon icon={faEllipsisH} />
    </a>
  ));

  return (
    <Card
      className={
        darkTheme
          ? "bg-dark shadow-sm border border-dark mb-3"
          : "bg-white shadow-sm border border-light mb-3"
      }
    >
      <Card.Header>
        <div className="d-flex mb-2">
          <h5 className="mb-0 text-uppercase flex-grow-1">
            2. VISUAL AWAKENING CONCLUSIONS
          </h5>
          <div>
            <ExcelFile
              element={
                <Button size="sm" className="text-uppercase mr-2">
                  <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                  <span>Export</span>
                </Button>
              }
            >
              <ExcelSheet
                data={conclusionsSnapshot?.docs ?? []}
                name="Visual Awakening Conclusions"
              >
                <ExcelColumn
                  label="Creation time"
                  value={(item) =>
                    moment(
                      convertSecondstoMilliSeconds(
                        item?.data()?.created_at?.seconds,
                        item?.data()?.created_at?.nanoseconds
                      )
                    ).format("LLL")
                  }
                />
                <ExcelColumn
                  label="Name"
                  value={(item) => item.data().created_by?.name}
                />
                <ExcelColumn
                  label="Email"
                  value={(item) => item.data().created_by?.email}
                />
                <ExcelColumn
                  label="Title"
                  value={(item) => item.data().title}
                />
                <ExcelColumn
                  label="Description"
                  value={(item) => item.data().description}
                />
              </ExcelSheet>
            </ExcelFile>

            <Button
              onClick={() => {
                setShowConclusionForm(true);
                resetConclusion();
              }}
              className="text-uppercase"
              size="sm"
            >
              Add conclusion
            </Button>
          </div>
        </div>
        <div>
          <p className="mb-0">
            "Is your industry perimeter a Red Ocean? Why?", "Define the standard
            offering on your industry" and "Is there a potential to
            differentiate from the standard offering?Some ideas of new value
            elements to be added in the offering?"
          </p>
        </div>
      </Card.Header>

      <Card.Body>
        {conslusionsError && (
          <p className="text-center">
            Error fetching conclusions, please reload.
          </p>
        )}

        {conclusionsLoading && (
          <p className="text-center">
            <Spinner animation="border" size="sm" /> Fetching conclusions...
          </p>
        )}

        {conclusionsSnapshot?.docs?.length > 0 ? (
          conclusionsSnapshot?.docs?.map((item, index) => (
            <div key={index} className="border rounded p-2 mb-2">
              <div className="d-flex justify-content-between">
                <p className="mb-0">
                  <b>{index + 1}.</b> {item.data().title}
                </p>
                <div className="d-flex">
                  <Dropdown className="px-1 py-0 pointer">
                    <Dropdown.Toggle
                      size="sm"
                      variant="white"
                      as={CustomToggle}
                    />

                    <Dropdown.Menu>
                      <Dropdown.Item
                        onClick={() => {
                          setEditing(true);
                          setConclusion({
                            id: item.id,
                            title: item.data().title,
                            description: item.data().description,
                          });
                          setShowConclusionForm(true);
                        }}
                        style={{
                          textDecoration: "none",
                          cursor: "pointer",
                          width: "100%",
                        }}
                        className="text-uppercase text-dark"
                      >
                        <FontAwesomeIcon icon={faEdit} /> Edit
                      </Dropdown.Item>

                      <Dropdown.Item
                        onClick={() => {
                          setShowDeleteModal(true);
                          setConclusion({
                            id: item.id,
                            title: item.data().title,
                            description: item.data().description,
                          });
                        }}
                        className="text-uppercase"
                      >
                        <FontAwesomeIcon icon={faTrash} /> Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
              <p className="small mb-0">{item.data().description}</p>
              {/* <p className="mb-0 small">
                {moment(
                  convertSecondstoMilliSeconds(
                    item?.data()?.created_at?.seconds,
                    item?.data()?.created_at?.nanoseconds
                  )
                ).format("LLL")}{" "}
                <b className="text-muted">
                  ({item?.data()?.created_by?.email})
                </b>
              </p> */}
            </div>
          ))
        ) : (
          <p className="text-center">
            <i>No conclusions added yet.</i>
          </p>
        )}
      </Card.Body>

      {/* Add and Edit conclusion modal */}
      <Modal
        show={showConclusionForm}
        onHide={() => {
          setShowConclusionForm(false);
          resetConclusion();
        }}
      >
        <Modal.Header closeButton>
          <div>
            <h5>{editing ? "Edit" : "Add"} conclusion</h5>
            <p className="mb-0 small text-muted">
              Marked (<span className="text-danger">*</span>) fields are
              mandatory.
            </p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleFormSubmit}>
            <Form.Group>
              <Form.Label>
                Title <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter conclusion title"
                value={conclusion?.title}
                onChange={(e) => {
                  setConclusion((prev) => {
                    let newValues = { ...prev };
                    newValues["title"] = e.target.value;
                    return newValues;
                  });
                  setError(false);
                }}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>
                Description <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Enter conclusion description"
                value={conclusion?.description}
                onChange={(e) => {
                  setConclusion((prev) => {
                    let newValues = { ...prev };
                    newValues["description"] = e.target.value;
                    return newValues;
                  });
                  setError(false);
                }}
              />
            </Form.Group>

            {error && (
              <p className="text-danger small">
                <b>Note:</b> Title, description cannot be empty
              </p>
            )}

            <div className="d-flex">
              <Button
                variant="primary"
                type="submit"
                className="mr-2 text-uppercase"
                size="sm"
                disabled={sending}
              >
                {sending ? (
                  <span>
                    <Spinner animation="border" size="sm" /> Sending...
                  </span>
                ) : (
                  <span>{editing ? "Update" : "Add"}</span>
                )}
              </Button>
              <Button
                variant="none"
                className="text-uppercase shadow-sm"
                size="sm"
                onClick={() => {
                  setShowConclusionForm(false);
                  resetConclusion();
                }}
              >
                Discard
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete conclusion modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          resetConclusion();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <p>
              Are you sure you want to delete conclusion,{" "}
              <b>"{conclusion?.title}"</b>?
            </p>
            <p className="small text-muted">
              <b>Note:</b> Once deleted you cannot retrive.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div>
            <Button
              variant="danger"
              size="sm"
              className="mr-2"
              onClick={handleDeleteConclusion}
              disabled={deleting}
            >
              {deleting ? (
                <span>
                  <Spinner animation="border" size="sm" /> Deleting...
                </span>
              ) : (
                <span>Yes, Delete</span>
              )}
            </Button>
            <Button
              variant="none"
              size="sm"
              className="shadow-sm"
              onClick={() => {
                setShowDeleteModal(false);
                resetConclusion();
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}

const mapStateToProps = (state) => ({
  darkTheme: state.darkTheme,
});

export default connect(mapStateToProps)(AsIsConclusions);
